/**
 * Device presence honesty.
 *
 * Lifecycle labels (`active`) are not online. `updatedAt` is not last-seen.
 * A device with no heartbeat / no gateway / unknown status counts as
 * offline so 5 silent devices cannot be 0 online AND 0 offline.
 * This helper never marks a device as emitting.
 */

export const DEFAULT_OFFLINE_AFTER_MS = 15 * 60 * 1000;

export type DevicePresence = 'online' | 'offline' | 'unknown';

export type DevicePresenceCounts = {
  total: number;
  online: number;
  offline: number;
  unknown: number;
};

export function parseHeartbeat(value: unknown): Date | null {
  if (value == null || value === '') return null;
  const d = value instanceof Date ? value : new Date(String(value));
  const t = d.getTime();
  if (!Number.isFinite(t)) return null;
  return d;
}

export function classifyDevicePresence(
  input: {
    lastTelemetryAt?: unknown;
    lastSeen?: unknown;
    updatedAt?: unknown;
    createdAt?: unknown;
    status?: unknown;
    gatewayId?: unknown;
  } | null | undefined,
  now: number = Date.now(),
  offlineAfterMs: number = DEFAULT_OFFLINE_AFTER_MS
): DevicePresence {
  const src = input && typeof input === 'object' ? input : {};
  const beat = parseHeartbeat(src.lastTelemetryAt) || parseHeartbeat(src.lastSeen);
  if (beat) {
    return now - beat.getTime() <= offlineAfterMs ? 'online' : 'offline';
  }
  return 'offline';
}

export function countDevicePresence(
  devices: Array<Parameters<typeof classifyDevicePresence>[0]>,
  now: number = Date.now(),
  offlineAfterMs: number = DEFAULT_OFFLINE_AFTER_MS
): DevicePresenceCounts {
  const counts: DevicePresenceCounts = { total: 0, online: 0, offline: 0, unknown: 0 };
  for (const device of devices || []) {
    counts.total += 1;
    counts[classifyDevicePresence(device, now, offlineAfterMs)] += 1;
  }
  return counts;
}

export function presenceIsClosed(counts: DevicePresenceCounts): boolean {
  return counts.online + counts.offline + counts.unknown === counts.total;
}

export function lastSeenDisplay(lastSeen: unknown): string {
  const beat = parseHeartbeat(lastSeen);
  return beat ? beat.toLocaleString() : '—';
}

export function presenceHeadline(counts: DevicePresenceCounts): string {
  return `${counts.total} total • ${counts.online} online • ${counts.offline} offline • ${counts.unknown} unknown`;
}
